/**
 * PD-25 ScoutSubAppView — route-level wiring shim for <ScoutIframeMount>.
 *
 * Responsibility: assemble the props `<ScoutIframeMount>` needs (inputs,
 * controllerOptions, postmessage_origin_allowlist) from safe defaults + URL
 * search params, so SubAppPage can stay a thin route file.
 *
 * SAFETY POSTURE (default-OFF reversibility per PD-25 plan §8 R10):
 *   - Default `runPreMountChecks` returns ONE failing check → H4 verdict =
 *     `block` → mount controller → `audit_failed` state → fallback panel
 *     rendered (NEVER an `<iframe>` element). This guarantees that even if
 *     `PD25_SCOUT_MOUNT_ENABLED` accidentally flips on in prod, NO iframe is
 *     actually mounted until a future polish wave wires the real H4 audit
 *     runner + M2 store accessor + M3 handshake store + production origin
 *     allow-list.
 *   - Test mode override: `?pd25_test_mode=allow_all_checks` opts into a
 *     pass-all stub for E2E coverage; never the default.
 *
 * Scope: SHELL-SIDE ONLY. No external Scout app. No backend. No tracker.
 */
import { useMemo } from 'react';
import { BridgeHandshakeStore } from '../../bridge/handshake';
import { ScoutIframeMount } from './ScoutIframeMount';
import { PD25_SCOUT_MOUNT_ENABLED } from './feature-flag';
import type {
  RunPreMountChecks,
  ScoutIframeMountControllerOptions,
} from './controller';
import type { ScoutIframeMountInputs } from './types';

/** Returns a single failing check — H4 will block the mount → fallback panel. */
const DEFAULT_RUN_PRE_MOUNT_CHECKS: RunPreMountChecks = () => [
  {
    check_number: 1,
    check_name: 'browser_cookie_api_available',
    outcome: 'fail',
    detail: 'PD-25 wiring shim default — replace via prod injection',
  },
];

/** Pass-all stub used ONLY when `?pd25_test_mode=allow_all_checks` is present. */
const PASS_ALL_PRE_MOUNT_CHECKS: RunPreMountChecks = () => [
  { check_number: 1, check_name: 'browser_cookie_api_available', outcome: 'pass' },
  { check_number: 2, check_name: 'scout_origin_configured', outcome: 'pass' },
  { check_number: 3, check_name: 'scout_origin_reachable', outcome: 'pass' },
  { check_number: 4, check_name: 'cookie_api_responds', outcome: 'pass' },
  { check_number: 5, check_name: 'not_nested_in_third_party', outcome: 'pass' },
];

export interface ScoutSubAppViewProps {
  /** Override the feature flag in tests; production callers leave undefined. */
  enabledOverride?: boolean;
  /** Override URL search-param reading in tests. */
  urlSearchOverride?: URLSearchParams;
  /** Inject a controller's `runPreMountChecks` for unit tests. */
  runPreMountChecksOverride?: RunPreMountChecks;
}

/**
 * Renders <ScoutIframeMount> when the feature flag is on. Default-safe:
 * even when rendered, the controller drops to `audit_failed` because the
 * default H4 stub fails — no `<iframe>` element is ever created in prod.
 */
export function ScoutSubAppView(props: ScoutSubAppViewProps = {}): React.ReactElement | null {
  const enabled = props.enabledOverride ?? PD25_SCOUT_MOUNT_ENABLED;
  if (!enabled) return null;

  const search = useMemo(() => {
    if (props.urlSearchOverride !== undefined) return props.urlSearchOverride;
    try {
      return new URLSearchParams(window.location.search);
    } catch {
      return new URLSearchParams('');
    }
  }, [props.urlSearchOverride]);

  const testMode = search.get('pd25_test_mode');
  const srcOverride = search.get('scout_src');

  const inputs = useMemo<ScoutIframeMountInputs>(
    () => ({
      src_url: srcOverride ?? 'about:blank',
      src_kind: testMode === 'allow_all_checks' ? 'controlled_fixture' : 'production_origin',
      allowed_origin_suffixes: ['.example.com'],
      iframe_mount_id: 'scout-default-mount',
      handshake_request_id: 'pd25-route-req',
      handshake_hmac: 'unsigned:route-wiring-placeholder',
    }),
    [srcOverride, testMode],
  );

  const controllerOptions = useMemo<ScoutIframeMountControllerOptions>(
    () => ({
      runPreMountChecks:
        props.runPreMountChecksOverride ??
        (testMode === 'allow_all_checks' ? PASS_ALL_PRE_MOUNT_CHECKS : DEFAULT_RUN_PRE_MOUNT_CHECKS),
      handshakeStore: new BridgeHandshakeStore(),
    }),
    [props.runPreMountChecksOverride, testMode],
  );

  return (
    <ScoutIframeMount
      inputs={inputs}
      controllerOptions={controllerOptions}
      postmessage_origin_allowlist={[]}
      enabled={true /* parent already gated; pass through */}
    />
  );
}
